import {
  Box,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../../locales/messages";
import { reviewTokens } from "../../theme";

export type CommentStatusFilter = "ALL" | "OPEN" | "RESOLVED";

interface Props {
  status: CommentStatusFilter;
  onStatusChange: (s: CommentStatusFilter) => void;
  counts: { all: number; open: number; resolved: number };
  search: string;
  onSearchChange: (s: string) => void;
  refreshing?: boolean;
}

export function CommentsFilterBar({
  status,
  onStatusChange,
  counts,
  search,
  onSearchChange,
  refreshing,
}: Props) {
  const { t } = useTranslation();

  const handleChange = (
    _: React.MouseEvent<HTMLElement>,
    next: CommentStatusFilter | null,
  ) => {
    if (next) onStatusChange(next);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ flexWrap: "wrap", rowGap: 1, columnGap: 1 }}
    >
      <ToggleButtonGroup
        value={status}
        exclusive
        onChange={handleChange}
        size="small"
        data-testid="comments-filter-tabs"
        sx={{
          bgcolor: reviewTokens.slate50,
          border: `1px solid ${reviewTokens.border}`,
          borderRadius: `${reviewTokens.radiusMd}px`,
          p: 0.25,
          "& .MuiToggleButton-root": {
            border: "none",
            borderRadius: `${reviewTokens.radiusSm}px !important`,
            textTransform: "none",
            fontWeight: 500,
            fontSize: 12,
            px: 1.25,
            py: 0.5,
            color: "text.secondary",
            "&:hover": { bgcolor: "transparent", color: "text.primary" },
            "&.Mui-selected": {
              bgcolor: "background.paper",
              color: "text.primary",
              boxShadow: `0 1px 2px rgba(15, 23, 42, 0.06)`,
              "&:hover": { bgcolor: "background.paper" },
            },
          },
        }}
      >
        <ToggleButton value="OPEN" data-testid="comments-filter-open">
          {t(messages.Comments_Open())} ({counts.open})
        </ToggleButton>
        <ToggleButton value="RESOLVED" data-testid="comments-filter-resolved">
          {t(messages.Comments_Resolved())} ({counts.resolved})
        </ToggleButton>
        <ToggleButton value="ALL" data-testid="comments-filter-all">
          {t(messages.Comments_All())} ({counts.all})
        </ToggleButton>
      </ToggleButtonGroup>
      <Stack direction="row" alignItems="center" spacing={1}>
        {refreshing && (
          <Box data-testid="comments-refreshing">
            <CircularProgress size={14} />
          </Box>
        )}
        <TextField
          size="small"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t(messages.Search_Comments())}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 220,
            "& .MuiOutlinedInput-root": { fontSize: 13 },
          }}
          data-testid="comments-search"
        />
      </Stack>
    </Stack>
  );
}
